import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { api, hlaskaZChyby } from '../api/client.js';
import { Btn, Chyba, Dialog, Input, Pole } from '../components/ui.js';

/**
 * Samoobslužná změna hesla (terén Menu i kancelář) — vyžaduje současné heslo,
 * nové podle pravidel registrace (8+, písmeno a číslice, živá kontrola).
 */
export function ZmenaHeslaDialog({ onZavrit }: { onZavrit: () => void }) {
  const [soucasne, setSoucasne] = useState('');
  const [nove, setNove] = useState('');
  const [zobrazit, setZobrazit] = useState(false);
  const [hotovo, setHotovo] = useState(false);

  const noveOk = nove.length >= 8 && /[A-Za-zÀ-ž]/.test(nove) && /\d/.test(nove);

  const zmenit = useMutation({
    mutationFn: () => api.patch<{ ok: boolean }>('/api/auth/heslo', { soucasne, nove }),
    onSuccess: () => setHotovo(true),
  });

  const odeslat = (e: FormEvent) => {
    e.preventDefault();
    zmenit.mutate();
  };

  if (hotovo) {
    return (
      <Dialog nadpis="Heslo změněno" onZavrit={onZavrit}>
        <p className="flex items-center gap-2 text-base">
          <CheckCircle2
            size={20}
            strokeWidth={2}
            aria-hidden="true"
            className="text-success-text"
          />
          Od teď se přihlašujete novým heslem.
        </p>
        <Btn className="mt-4 w-full" onClick={onZavrit}>
          Zavřít
        </Btn>
      </Dialog>
    );
  }

  return (
    <Dialog nadpis="Změnit heslo" onZavrit={onZavrit}>
      <form onSubmit={odeslat} className="flex flex-col gap-4">
        <Pole popisek="Současné heslo" povinne>
          <Input
            type={zobrazit ? 'text' : 'password'}
            autoComplete="current-password"
            required
            value={soucasne}
            onChange={(e) => setSoucasne(e.target.value)}
          />
        </Pole>
        <Pole
          popisek="Nové heslo"
          povinne
          napoveda={noveOk ? undefined : 'aspoň 8 znaků, písmeno a číslice'}
        >
          <Input
            type={zobrazit ? 'text' : 'password'}
            autoComplete="new-password"
            required
            minLength={8}
            value={nove}
            onChange={(e) => setNove(e.target.value)}
          />
          {noveOk && (
            <p className="mt-1 flex items-center gap-1 text-sm text-success-text">
              <CheckCircle2 size={14} strokeWidth={2} aria-hidden="true" /> Heslo je v pořádku.
            </p>
          )}
        </Pole>
        <label className="flex min-h-11 items-center gap-3">
          <input
            type="checkbox"
            className="size-6"
            checked={zobrazit}
            onChange={(e) => setZobrazit(e.target.checked)}
          />
          <span className="text-base">Zobrazit hesla</span>
        </label>
        {zmenit.error ? <Chyba>{hlaskaZChyby(zmenit.error)}</Chyba> : null}
        <Btn type="submit" className="w-full" disabled={zmenit.isPending}>
          {zmenit.isPending ? 'Ukládám…' : 'Uložit nové heslo'}
        </Btn>
      </form>
    </Dialog>
  );
}
